import { useEffect, useMemo } from "react";
import { SlideUpModal } from "src/components/SlideUpModal";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import {
  toCreateAssetReq,
  toUpdateAssetReq,
  type AssetFormData,
} from "src/api/adapters/assets";
import { account_type, type UpdatedAccountTypeOption } from "src/constants";
import {
  greaterThanMinAmount,
  notEmpty,
  validate,
  test,
  type ValidationMapperFunc,
} from "src/utils";
import {
  CreditCardIcon,
  FinancialAccountFields,
  FormTable,
  FormTableBody,
} from "@porchpass/ui";
import {
  financialAccountErrorsAfterSubmit,
  fromUiFinancialAccountPatch,
  toUiFinancialAccount,
} from "src/forms/adapters/financialAccount";

const validationMapper: ValidationMapperFunc<AssetFormData> = (_) => ({
  account_type: [test(notEmpty, "Must provide an account type")],
  institution_name: [test(notEmpty, "Must provide a name for the bank")],
  balance_amount: [
    test(notEmpty, "Must provide an amount"),
    test(greaterThanMinAmount(0), "Amount must be greater than 0"),
  ],
});

type AddFianncialAccountModalProps = {
  financialAccount: AssetFormData;
  availableAccountOptions: UpdatedAccountTypeOption[];
  closeHandler: () => void;
  confirmHandler: () => void;
};

export default ({
  financialAccount,
  availableAccountOptions,
  closeHandler,
  confirmHandler,
}: AddFianncialAccountModalProps) => {
  const api = useWebClientApi();
  const { formData, setFormData, updateField } = useFormLoad<AssetFormData>(
    { ...financialAccount },
    () => Promise.resolve({}),
    () => ({}),
    { enabled: false },
  );

  useEffect(() => {
    setFormData({ ...financialAccount });
  }, [financialAccount, setFormData]);

  const [submitHandler, loading, , hasAttemptedFormSubmission] =
    useDlaFormSubmit(
      async (data: AssetFormData) => {
        if (financialAccount.id) {
          return api.updateAsset(financialAccount.id, toUpdateAssetReq(data));
        }
        return api.createAsset(toCreateAssetReq(data));
      },
      { onSuccess: () => confirmHandler() },
    );

  const formErrors = validate<AssetFormData>(formData, validationMapper);

  const selectedAccountType = account_type.find(
    (accountType) => accountType.id === financialAccount.account_type,
  );

  const accountOptions =
    selectedAccountType !== undefined
      ? [...availableAccountOptions, selectedAccountType]
      : availableAccountOptions;

  const accountTypeOptions = accountOptions.map((option) => ({
    value: option.id,
    label: option.label,
  }));

  const accountValue = useMemo(
    () => toUiFinancialAccount(formData),
    [formData],
  );

  const accountErrors = useMemo(
    () =>
      financialAccountErrorsAfterSubmit(formErrors, hasAttemptedFormSubmission),
    [formErrors, hasAttemptedFormSubmission],
  );

  const applyAccountChange = (patch: Partial<typeof accountValue>) => {
    const mapped = fromUiFinancialAccountPatch(patch);
    for (const [key, val] of Object.entries(mapped)) {
      if (val !== undefined) {
        updateField(key as keyof AssetFormData)(val);
      }
    }
  };

  return (
    <SlideUpModal
      title={financialAccount.id ? "Edit bank account" : "Add bank account"}
      isOpen={true}
      openSession={financialAccount}
      closeHandler={closeHandler}
      actionButtonText={financialAccount.id ? "Save" : "Add account"}
      actionButtonClickHandler={() => void submitHandler(formData, formErrors)}
      isLoading={loading}
    >
      <FormTable
        title="Bank Account"
        headerIcon={
          <CreditCardIcon
            variant="bulk-rounded"
            size={16}
            className="text-primary"
          />
        }
      >
        <FormTableBody>
          <FinancialAccountFields
            account={accountValue}
            errors={accountErrors}
            accountTypeOptions={accountTypeOptions}
            onFieldChange={(field, value) => {
              applyAccountChange({ [field]: value });
            }}
          />
        </FormTableBody>
      </FormTable>
    </SlideUpModal>
  );
};
